import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { Collections, Search, Wiki } from '@local/client-contracts';
import { UButtonComponent } from '@local/ui-infra';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { EventsService, LogService } from '@shared/services';
import { CustomKeyboardEvent, KeyboardService } from '@shared/services/keyboard.service';
import { isKey, keyCodes, KeyName } from '@local/ts-infra';
import { Logger } from '@unleash-tech/js-logger';
import { isEqual } from 'lodash';
import { BehaviorSubject, debounceTime, filter, Subject, Subscription, take, takeUntil } from 'rxjs';
import { CollectionsService, CreateNewType } from 'src/app/bar/services/collections.service';
import { HubService } from 'src/app/bar/services/hub.service';
import { SearchOptions, SearchResultContext, SearchService, SearchSession } from 'src/app/bar/services/search';
import { StatsService } from 'src/app/bar/services/stats.service';
import { WikiCardsService } from 'src/app/bar/services/wikis/wiki-cards.service';
import { isWikiDraft, ScrollTrigger } from '../../../results';
import { CollectionPopupService } from '../../services/collection-popup.service';
import { collectionContent } from '../../helpers/collection.content';
import { CreateCollectionType } from '../../models';
import { CollectionsComponentFocused } from '../collection-view/collection-view.component';
import { SearchExpandableButtonComponent } from '../search-expandable-button/search-expandable-button.component';
import { SearchParamsService } from 'src/app/bar/services/search-params.service';
import { WikiCardPreviewService } from '../../services/wiki-card-preview.service';
import { CollectionsUtilService } from 'src/app/bar/services/collections-util.service';
import { VerificationsCardsCommandsService } from '../../services/verifications-cards-commands-service';
import { ShareOptionsService } from 'src/app/bar/services/share-options.service';
import { TreeNode } from 'primeng/api';
import { WikisService } from 'src/app/bar/services/wikis/wikis.service';
import { WikiTreeComponent } from '../wiki-tree/wiki-tree.component';
import { WikiTreeSourceSettings } from 'src/app/bar/services/search/client/wiki-tree/wiki-tree-source-settings';
import { WikiFoldersService } from 'src/app/bar/services/wikis/wiki-folders.service';
import { stopEvent } from '@shared/utils/elements-util';
import { WikiItemSelectionPopupService } from '../../services/wiki-item-selection-popup.service';
import { generateId } from '@local/common-web';

export type SelectedItem = { id: string; parentId: string; path?: Wiki.Path[]; type?: string; countSubLevels?: number };

@UntilDestroy()
@Component({
  selector: 'wiki-collection',
  templateUrl: './wiki-collection.component.html',
  styleUrls: ['./wiki-collection.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WikiCollectionComponent implements OnInit, OnDestroy {
  private readonly DELETED_MESSAGE = 'Deleting a folder will delete all the <br> cards inside that folder.';
  private _collection: Wiki.WikiCollection;
  private searchSubscription: Subscription;
  private _tree: TreeNode;

  @Input() set collection(val: Wiki.WikiCollection) {
    if (!val) return;

    const prevCollection = this._collection;
    this._collection = val;
    this.canEdit = this.collectionsUtilService.canEdit(this.collectionsService.currentCollectionView);

    if (!isEqual(prevCollection?.id, this.collection.id)) {
      this.resetData();
      this.collectionChanged.emit(true);
      this.cdr.markForCheck();
      this.search();
    } else {
      const policyChanged: boolean = this.collection?.policy && !isEqual(prevCollection?.policy, this.collection?.policy);
      if (policyChanged) {
        this.search();
      } else {
        if (this.collection?.cardIds?.length === 0) {
          this.resetData();
        }
      }
    }

    this.cdr.markForCheck();
  }
  get collection() {
    return this._collection;
  }

  @Input() state: CreateCollectionType;
  @Input() componentFocused: CollectionsComponentFocused;

  @Output() componentFocusedFinish = new EventEmitter();
  @Output() componentFocusedUpdate = new EventEmitter<CollectionsComponentFocused>();
  lastComponentFocused: CollectionsComponentFocused;

  collectionContent = collectionContent;

  //context menu
  CollectionsComponentFocused = CollectionsComponentFocused;

  // Search
  private searchSession: SearchSession;
  displayedContext$ = new BehaviorSubject<SearchResultContext>(null);
  itemsAmount = 0;
  searchModel: string;
  formInputChanged = new Subject<string>();
  emptyResults$ = new BehaviorSubject<boolean>(false);
  tree$ = new BehaviorSubject<TreeNode>({});
  loading: boolean;

  //checkbox
  clearSelectedNodes: EventEmitter<boolean> = new EventEmitter();
  selectedItems: SelectedItem[];
  cardIdsSelected: string[];

  //keyboard
  private keyHandlerId: string;

  protected logger: Logger;
  collectionChanged = new EventEmitter();
  addCardTooltip: string;
  canEdit: boolean;
  onCreatedFolder: EventEmitter<boolean> = new EventEmitter();
  ghostItems: number[];
  cardCreationProcess: boolean;

  @ViewChild(WikiTreeComponent) searchResults: WikiTreeComponent;
  @ViewChild(SearchExpandableButtonComponent) searchButton: SearchExpandableButtonComponent;
  @ViewChild('openCollectionButtonRef', { read: UButtonComponent }) openCollectionButtonRef: UButtonComponent;

  //preview
  previewMode: boolean;
  showPreviewButton: boolean;

  get previewHandlerService() {
    return this.searchResults?.previewHandlerService;
  }

  get isPreviewMode(): boolean {
    return this.previewMode && this._tree?.children?.length > 0;
  }

  set tree(value: TreeNode) {
    this._tree = value;
    this.tree$.next(value);
    this.cdr.markForCheck();
  }

  isPreviewPressed: boolean;
  @Input() updateHeight: EventEmitter<void>;

  constructor(
    private hubService: HubService,
    private cdr: ChangeDetectorRef,
    public collectionPopupService: CollectionPopupService,
    private keyboardService: KeyboardService,
    public collectionsService: CollectionsService,
    private eventsService: EventsService,
    private searchService: SearchService,
    logService: LogService,
    private wikiCardsService: WikiCardsService,
    private statsService: StatsService,
    public searchParamsService: SearchParamsService,
    private wikiCardPreviewService: WikiCardPreviewService,
    private collectionsUtilService: CollectionsUtilService,
    private verificationsCardsCommandsService: VerificationsCardsCommandsService,
    private shareOptionsService: ShareOptionsService,
    private wikisService: WikisService,
    private wikiFolders: WikiFoldersService,
    private wikiItemSelectionPopupService: WikiItemSelectionPopupService
  ) {
    this.searchSession = this.searchService.getOrCreateSearchSession('wiki-collections-collections');
    this.logger = logService.scope('WikiCollectionComponent');
  }

  ngOnInit(): void {
    this.searchModel = '';
    this.formInputChanged.pipe(debounceTime(300), untilDestroyed(this)).subscribe((search) => {
      this.onSearchItems(search);
    });
    this.registerKeyboardHandler();
    this.wikiCardPreviewService.wikiCardPopupStatus.pipe(untilDestroyed(this)).subscribe((res) => {
      if (res) {
        if (this.componentFocused) {
          this.lastComponentFocused = this.componentFocused;
          this.componentFocusedUpdate.emit(null);
        }
      } else {
        this.componentFocusedUpdate.emit(this.lastComponentFocused);
      }
    });
    this.wikisService.createdCard$.pipe(untilDestroyed(this)).subscribe(() => {
      if (this.cardCreationProcess) {
        this.setGhostState('remove');
        this.cardCreationProcess = false;
      }
    });
  }

  ngOnDestroy(): void {
    this.unregisterKeyHandler();
    this.searchSession.destroy();
  }

  selectedItemsUpdate(selectedItems: SelectedItem[]) {
    this.eventsService.event('collections.select_remove_item_collection', {
      location: { title: this.hubService.currentLocation },
    });
    this.selectedItems = selectedItems;
    this.cardIdsSelected = this.selectedItems?.map((s) => s.id)?.filter((id) => !id.startsWith(this.wikisService.FOLDER_PREFIX)) || [];
    this.cdr.markForCheck();
  }

  resetData() {
    if (this.searchButton) {
      this.searchButton.resetData();
    }
    this.searchModel = '';
    this.itemsAmount = this.tree?.data?.itemsCount || 0;
    this.collectionsService.collectionResultData$.next({ count: this.itemsAmount });
    this.clearSelectedNodes.emit(true);
    this.displayedContext$.next(null);
    this.loading = false;
    this.cdr.markForCheck();
  }

  onSearchItems($event) {
    $event = !$event || $event.length === 0 ? null : $event;
    this.searchModel = $event;
    this.hubService.query = $event;
    this.search();
  }

  onDynamicCommand($event: { value: string; item: Search.WikiCardCollectionResultItem }) {
    const commandType: string = $event.value;
    const item = $event.item;
    switch (commandType) {
      case 'remove':
      case 'delete-draft': {
        const path = item?.path;
        this.openDeletePopup([{ id: item?.id, parentId: path?.[path.length - 1]?.id }], isWikiDraft(item));
        break;
      }
      case 'rename':
        this.renameFromCommand(item);
        break;
      case 'make-a-copy':
        this.wikiItemSelectionPopupService.duplicateCard(item);
        break;
    }
    if (this.verificationsCardsCommandsService.isVerificationCommand(commandType)) {
      this.verificationsCardsCommandsService.executeCommand(commandType, item);
    }
  }

  async renameFromCommand(item: Search.WikiCardCollectionResultItem) {
    item.showResultSections.showInputTitle = true;
    this.cdr.markForCheck();
  }

  async titleChanged(event: { value: string; item: Search.WikiCardCollectionResultItem }) {
    const id = event.item.id;
    const newTitle = event.value;
    if (!newTitle) {
      return;
    }
    if (id.startsWith(this.wikisService.FOLDER_PREFIX)) {
      const updateActions: Collections.UpdateAction[] = [{ field: 'title', type: 'Update', value: newTitle }];
      await this.wikiFolders.update(id, updateActions);
    } else {
      await this.wikiCardsService.update({
        card: { id, title: newTitle, collectionId: event.item.collectionId },
        updateModifiedTime: true,
      });
    }
  }

  private async onDeleteItems(items: SelectedItem[]) {
    this.eventsService.event('collections.remove_item_collection', {
      location: { title: this.hubService.currentLocation },
      label: 'remove',
    });
    this.clearSelectedItems();
    if (items.length === 1) {
      await this.wikisService.deleteWikiItem({ itemId: items[0]?.id, parentId: items[0]?.parentId });
    } else {
      await this.deleteMany(items);
    }
  }

  private async deleteMany(deleted: SelectedItem[]) {
    const items = this.removeSelectedParents(deleted);
    const divideByParentId = this.divideByParentId(items);
    const deletedItems = Object.entries(divideByParentId).map(([parentId, ids]) => ({
      ids,
      parentId,
    }));
    await this.wikisService.deleteMany({ items: deletedItems });
  }

  private removeSelectedParents(arr: SelectedItem[]): SelectedItem[] {
    const ids = new Set(arr.map((item) => item.id));
    return arr.filter((item) => !ids.has(item.parentId));
  }

  private divideByParentId(arr: SelectedItem[]): { [key: string]: string[] } {
    return arr.reduce(
      (acc, item) => {
        if (!acc[item.parentId]) {
          acc[item.parentId] = [];
        }
        acc[item.parentId].push(item.id);
        return acc;
      },
      {} as { [key: string]: string[] }
    );
  }

  changeFocus(focus: string) {
    this.collectionsService.onFocusChange = focus;
  }

  updateVerificationStatus(status: 'Verify' | 'Unverify') {
    const areYouSure = this.wikiCardsService.changeVerificationStatus(status, this.cardIdsSelected?.length);
    areYouSure.compInstance.primaryButton.pipe(takeUntil(areYouSure.destroy$)).subscribe(() => {
      if (this.cardIdsSelected?.length) {
        this.wikiCardsService.updateVerificationStatus(status === 'Verify' ? 'Verified' : 'Unverified', this.cardIdsSelected, false);
      }
      this.clearSelectedItems();
    });
  }

  clearSelectedItems() {
    this.clearSelectedNodes.emit(true);
    this.cdr.markForCheck();
  }

  openDeletePopup(items: SelectedItem[], isUnpublishedCard = false) {
    let itemString = 'item';
    const includeFolder = items?.map((d) => d.id).some((id) => id.startsWith(this.wikisService.FOLDER_PREFIX));
    if (items.length === 1) {
      itemString = includeFolder ? 'folder' : isUnpublishedCard ? 'draft' : 'card';
    }
    const deletePopupRef = this.collectionPopupService.openAreYouSureToRemoveItemPopup(
      null,
      items.length,
      itemString,
      includeFolder ? this.DELETED_MESSAGE : null
    );
    deletePopupRef.compInstance.primaryButton.pipe(take(1)).subscribe(async () => {
      await this.onDeleteItems(items);
    });
  }

  openMovePopup(items: SelectedItem[]) {
    const id = generateId();
    this.collectionsService.openSelectPopup$.next({
      id,
      data: null,
      type: 'move-to-wiki-popup',
      options: { doSetup: true, data: { context: { items } } },
    });
  }

  openNewCollectionPopup(elm: UButtonComponent) {
    const newItemsPopup = this.collectionsService.openNewCollectionPopup(elm.button.nativeElement, false, [], 'wiki');

    newItemsPopup.compInstance.onClickEvent.pipe(take(1)).subscribe(async (kind: CreateNewType) => {
      if (kind == 'Card') {
        this.cardCreationProcess = true;
        this.setGhostState('add');
      }
      if (kind === 'Folder') {
        await this.createFolder();
      }
    });
  }

  async createFolder() {
    this.setGhostState('add', 1);
    await this.wikisService.createFolder({ wikiId: this.collection?.id });
    this.setGhostState('remove', 1);
    setTimeout(() => {
      this.onCreatedFolder.emit(true);
    }, 10);
  }

  async onCreatedChild(type: string) {
    switch (type) {
      case 'folder':
        await this.createFolder();
        break;
      case 'card':
        await this.collectionsUtilService.openWikiCard(this.collection?.id);
        break;
    }
  }

  setGhostState(state: 'add' | 'remove', line = 2) {
    if (!this.ghostItems?.length) {
      this.ghostItems = [];
    }
    if (state === 'add') {
      this.ghostItems.unshift(line);
    } else if (state === 'remove') {
      const i = this.ghostItems.findIndex((g) => g === line);
      if (i !== -1) {
        this.ghostItems.splice(i, 1);
      }
    }
    this.cdr.markForCheck();
  }

  //@@@@ SEARCH PART
  private search() {
    delete this.loading;
    this.collectionChanged.emit(false);
    this.cdr.markForCheck();
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
      this.searchSubscription = null;
    }
    const options: SearchOptions = {
      resetSession: false,
      query: this.searchModel,
      sources: [
        {
          id: 'wiki-tree',
          wikiId: this.collection.id,
          type: 'wiki-tree',
          noHeader: true,
        } as WikiTreeSourceSettings,
      ],
      trigger: 'navigation_tree',
      telemetrySearchMethod: 'Quick-Search',
    };

    let times = 0;
    this.searchSubscription = this.searchSession
      .search$(options)
      .pipe(untilDestroyed(this))
      .subscribe((ctx: SearchResultContext) => {
        if (ctx.searchCompleted) {
          this.loading = ++times != 2 && !ctx.items.length;
          this.cdr.markForCheck();
        }

        if (ctx.items.length || ctx.searchCompleted) this.onSearchCompleted(ctx);
      });
  }

  onSearchCompleted(ctx: SearchResultContext) {
    const tree = ctx.items?.[0] as TreeNode;
    if (!tree) {
      this.loading = false;
      this.cdr.markForCheck();
    }

    this.tree = tree;
    this.displayedContext$.next(ctx);
    this.loading = false;
    this.itemsAmount = tree?.data?.itemsCount || 0;
    this.collectionsService.collectionResultData$.next({ count: this.itemsAmount });
    this.emptyResults$.next(ctx && ctx.searchCompleted && tree?.children?.length == 0 && this.searchModel?.length > 0);
    this.cdr.markForCheck();
  }

  nextPage(trigger: ScrollTrigger) {
    this.searchSession
      .nextPage$(trigger, [{ type: 'wiki-tree' }])
      .pipe(untilDestroyed(this))
      .subscribe((ctx: SearchResultContext[]) => {
        if (ctx[0]?.searchCompleted) {
          this.onSearchCompleted(ctx[0]);
        }
      });
  }

  //@@@ KEY HANDLING

  registerKeyboardHandler() {
    if (this.keyHandlerId) return;
    this.keyHandlerId = this.keyboardService.registerKeyHandler((keys, event) => {
      this.handleKeys(keys, event);
    }, 9);
    this.cdr.markForCheck();
  }

  private unregisterKeyHandler() {
    if (!this.keyHandlerId) return;
    this.keyboardService.unregisterKeyHandler(this.keyHandlerId);
    this.keyHandlerId = null;
  }

  async handleKeys(keys: Array<KeyName>, event: CustomKeyboardEvent) {
    if (
      this.canEdit &&
      isKey(event, keyCodes.c) &&
      !this.wikiCardPreviewService.popupRef &&
      !this.shareOptionsService.shareOptionsPopupRef &&
      !this.collectionPopupService.isEmojiPopupRefOpen &&
      !this.wikiItemSelectionPopupService.popupRef &&
      this.componentFocused === 6
    ) {
      this.openNewCollectionPopup(this.openCollectionButtonRef);
      stopEvent(event);
    }
    if (isKey(event, keyCodes.ArrowDown)) {
      this.componentFocusedUpdate.emit(CollectionsComponentFocused.RESULTS);
    }
  }

  onFocusSearch() {
    this.changeFocus('search');
    this.componentFocusedUpdate.emit(CollectionsComponentFocused.SEARCH);
  }
}
